import { FormEvent, useState } from "react";
import { postAdminData } from "../../apis";
import { API } from "../../apis/config";
import {
  AdminEventCreatePayload,
  EVENT_TYPE,
  EventTypeCode,
} from "../../interfaces/AdminEvent";

const URL_CODE_LENGTH = 9;
const URL_CODE_ALPHANUM =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const URL_CODE_PATTERN = /^[A-Za-z0-9]{9}$/;

const TYPE_OPTIONS: { value: EventTypeCode; label: string }[] = [
  { value: EVENT_TYPE.OPENING_WAREHOUSE, label: "창고개방전" },
  { value: EVENT_TYPE.FESTA, label: "페스타" },
  { value: EVENT_TYPE.PAJUSPIEL, label: "파주슈필" },
];

function generateRandomUrlCode(): string {
  const bytes = new Uint8Array(URL_CODE_LENGTH);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < URL_CODE_LENGTH; i++) {
    out += URL_CODE_ALPHANUM[bytes[i]! % URL_CODE_ALPHANUM.length];
  }
  return out;
}

/** `YYYY-MM-DD`를 로컬 0시 0분으로 해석해 ISO 문자열로 변환 */
function toIsoAtLocalMidnight(dateYmd: string): string | null {
  const trimmed = dateYmd.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  const d = new Date(`${trimmed}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function EventRegisterForm() {
  const [name, setName] = useState("");
  const [urlCode, setUrlCode] = useState("");
  const [type, setType] = useState<EventTypeCode>(EVENT_TYPE.OPENING_WAREHOUSE);
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    const startDate = toIsoAtLocalMidnight(startLocal);
    const endDate = toIsoAtLocalMidnight(endLocal);
    if (!startDate || !endDate) {
      setError("시작·종료 일자를 모두 입력해 주세요.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError("종료 일자는 시작 일자와 같거나 이후여야 합니다.");
      return;
    }

    const payload: AdminEventCreatePayload = {
      name: name.trim(),
      urlCode: urlCode.trim(),
      type,
      startDate,
      endDate,
    };
    if (!payload.name) {
      setError("행사명을 입력해 주세요.");
      return;
    }
    if (!URL_CODE_PATTERN.test(payload.urlCode)) {
      setError(
        "URL 코드는 영문 대소문자와 숫자만 사용해 정확히 9자리여야 합니다.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await postAdminData<unknown>(API.ADMIN.EVENTS, payload);
      if (!res.isSuccess) {
        setError(res.message || "행사 등록에 실패했습니다.");
        return;
      }
      setMessage("행사가 등록되었습니다.");
      setName("");
      setUrlCode("");
      setType(EVENT_TYPE.OPENING_WAREHOUSE);
      setStartLocal("");
      setEndLocal("");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "요청 중 오류가 발생했습니다.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-left">
      <h3 className="text-lg font-semibold text-slate-900 mb-1">행사 등록</h3>
      <p className="text-sm text-slate-500 mb-6">
        행사명·유형, URL 코드, 시작·종료 일자를 입력합니다.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-xxl">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="event-name"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              행사명
            </label>
            <input
              id="event-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </div>

          <div>
            <label
              htmlFor="event-type"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              행사 유형
            </label>
            <select
              id="event-type"
              value={type}
              onChange={(e) => setType(Number(e.target.value) as EventTypeCode)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value}: {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label
            htmlFor="event-url-code"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            URL 코드 ({URL_CODE_LENGTH}자리, 영문대문자·영문소문자·숫자)
          </label>
          <div className="flex gap-2 items-stretch">
            <input
              id="event-url-code"
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              value={urlCode}
              onChange={(e) => {
                const v = e.target.value
                  .replace(/[^A-Za-z0-9]/g, "")
                  .slice(0, URL_CODE_LENGTH);
                setUrlCode(v);
              }}
              required
              minLength={URL_CODE_LENGTH}
              maxLength={URL_CODE_LENGTH}
              pattern="[A-Za-z0-9]{9}"
              title="영문 대소문자와 숫자 9자리"
              className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 font-mono tracking-wide"
            />
            <button
              type="button"
              onClick={() => setUrlCode(generateRandomUrlCode())}
              className="shrink-0 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 whitespace-nowrap"
            >
              무작위 코드 생성
            </button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="event-start"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              시작 일자
            </label>
            <input
              id="event-start"
              type="date"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </div>
          <div>
            <label
              htmlFor="event-end"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              종료 일자
            </label>
            <input
              id="event-end"
              type="date"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </div>
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="text-sm text-emerald-700" role="status">
            {message}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
          >
            {submitting ? "등록 중…" : "행사 등록"}
          </button>
        </div>
      </form>
    </section>
  );
}
